/**
 * Small DOM helpers. No framework, no virtual DOM — just enough sugar to keep
 * the view modules readable.
 */

/**
 * Create an element.
 *   el('p', 'hello')
 *   el('button', { class: 'btn', onclick: fn }, 'Save')
 *   el('ul', {}, items.map((i) => el('li', i)))
 */
export function el(tag, props, children) {
  const node = document.createElement(tag);

  // Allow el(tag, children) when the second argument is clearly content.
  if (Array.isArray(props) || typeof props === 'string' || props instanceof Node) {
    children = props;
    props = {};
  }
  props = props || {};

  for (const [key, value] of Object.entries(props)) {
    if (value === null || value === undefined || value === false) continue;
    if (key === 'class' || key === 'className') node.className = value;
    else if (key === 'dataset') Object.assign(node.dataset, value);
    else if (key === 'style' && typeof value === 'object') Object.assign(node.style, value);
    else if (key === 'html') node.innerHTML = value;
    else if (key.startsWith('on') && typeof value === 'function') {
      node.addEventListener(key.slice(2).toLowerCase(), value);
    } else if (key === 'value' || key === 'checked' || key === 'disabled' || key === 'selected') {
      node[key] = value;
    } else {
      node.setAttribute(key, value === true ? '' : value);
    }
  }

  append(node, children);
  return node;
}

export function append(parent, children) {
  if (children === null || children === undefined || children === false) return parent;
  if (Array.isArray(children)) {
    for (const child of children) append(parent, child);
    return parent;
  }
  parent.append(children instanceof Node ? children : document.createTextNode(String(children)));
  return parent;
}

export function clear(node) {
  node.replaceChildren();
  return node;
}

export const $ = (selector, root = document) => root.querySelector(selector);
export const $$ = (selector, root = document) => [...root.querySelectorAll(selector)];

/** Escape text for the few places that build HTML strings (the print layouts). */
export function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** A labelled form field. */
export function field(labelText, control, hint) {
  const id = control.id || `f-${Math.random().toString(36).slice(2, 9)}`;
  control.id = id;
  return el('div', { class: 'field' }, [
    el('label', { class: 'field__label', for: id }, labelText),
    control,
    hint ? el('p', { class: 'field__hint' }, hint) : null,
  ]);
}

export function select(options, value, props = {}) {
  const node = el('select', { class: 'input', ...props });
  for (const option of options) {
    const { value: optionValue, label } =
      typeof option === 'string' ? { value: option, label: option } : option;
    node.append(el('option', { value: optionValue, selected: optionValue === value }, label));
  }
  // Keep a value that is no longer in the list (e.g. a renamed workstream on an
  // older entry) rather than silently reassigning the entry.
  if (value && node.value !== value) {
    node.prepend(el('option', { value, selected: true }, `${value} (not in list)`));
    node.value = value;
  }
  return node;
}

export function button(label, props = {}) {
  return el('button', { type: 'button', class: 'btn', ...props }, label);
}

export function icon(name) {
  // Small inline glyphs; text keeps it dependency-free and legible to screen
  // readers when paired with an accessible name on the control.
  const glyphs = { remove: '×', add: '+', prev: '‹', next: '›' };
  return el('span', { class: 'glyph', 'aria-hidden': 'true' }, glyphs[name] || name);
}

/**
 * A modal confirmation built on <dialog>. Resolves with the id of the button
 * pressed, or null if dismissed.
 */
export function confirmDialog({ title, body, actions, danger = false }) {
  return new Promise((resolve) => {
    let answer = null;
    const dialog = el('dialog', { class: `dialog${danger ? ' dialog--danger' : ''}` });
    const form = el('form', { method: 'dialog', class: 'dialog__form' }, [
      el('h2', { class: 'dialog__title' }, title),
      el('div', { class: 'dialog__body' }, body),
      el(
        'div',
        { class: 'dialog__actions' },
        actions.map((action) =>
          button(action.label, {
            class: `btn ${action.variant === 'primary' ? 'btn--primary' : action.variant === 'danger' ? 'btn--danger' : ''}`,
            disabled: action.disabled || false,
            dataset: { action: action.id },
            onclick: () => {
              answer = action.id;
              dialog.close();
            },
          }),
        ),
      ),
    ]);
    dialog.append(form);
    dialog.addEventListener('close', () => {
      dialog.remove();
      resolve(answer);
    });
    // Esc and backdrop clicks dismiss without choosing.
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    document.body.append(dialog);
    dialog.showModal();
    const first = dialog.querySelector('input, button:not([disabled])');
    if (first) first.focus();
  });
}

/** Brief, non-blocking confirmation message. */
let toastTimer = null;
export function toast(message) {
  let node = document.getElementById('toast');
  if (!node) {
    node = el('div', { id: 'toast', class: 'toast', role: 'status', 'aria-live': 'polite' });
    document.body.append(node);
  }
  node.textContent = message;
  node.classList.add('toast--visible');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => node.classList.remove('toast--visible'), 2600);
}

/** Trigger a file download from a Blob, entirely client-side. */
export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = el('a', { href: url, download: filename });
  document.body.append(link);
  link.click();
  link.remove();
  // Give Safari a moment before revoking, or the download can be cancelled.
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/** Ask the user for a file and resolve with its text contents. */
export function pickFile(accept = 'application/json') {
  return new Promise((resolve) => {
    const input = el('input', { type: 'file', accept, style: { display: 'none' } });
    input.addEventListener('change', async () => {
      const file = input.files && input.files[0];
      input.remove();
      if (!file) return resolve(null);
      resolve({ name: file.name, text: await file.text() });
    });
    // If the dialog is dismissed, no change event fires; the promise simply
    // never settles, which is harmless here as the caller is a click handler.
    document.body.append(input);
    input.click();
  });
}
